/**
 * Mission Data Helper
 * 
 * Utility for managing mission data transfer between Journey Guide and mission pages.
 * Uses sessionStorage for persistence across navigation and page refreshes.
 * 
 * USAGE EXAMPLES:
 * 
 * 1. In Journey Guide (JourneyGuidePage.js):
 *    import { setMissionData } from './utils/missionDataHelper';
 *    
 *    const handleStart = (route, intention) => {
 *      setMissionData({
 *        missionId: mission?.id,
 *        missionTitle: mission?.title,
 *        missionRoute: route,
 *        intention: intention,
 *        problemText: mission?.problemText
 *      });
 *      navigate(route, { state: { fromJourneyGuide: true, ... } });
 *    };
 * 
 * 2. In Mission Pages (RecommendationPage.js, QuickAdvicePage.js, etc.):
 *    import { getCombinedMissionData, isMissionMode } from './utils/missionDataHelper';
 *    
 *    const missionData = getCombinedMissionData(location.state);
 *    const isMission = isMissionMode(); // or missionData?.fromJourneyGuide
 *    const intention = missionData?.intention;
 * 
 * 3. Using the React Hook:
 *    import { useMissionData } from './utils/missionDataHelper';
 *    
 *    const { missionData, isMission, clearMission, getField } = useMissionData();
 *    const intention = getField('intention');
 */

const MISSION_DATA_KEY = 'journey_guide_mission_data';
const MISSION_DATA_EXPIRY = 30 * 60 * 1000; // 30 minutes
let missionDataCacheRaw = null;
let missionDataCacheParsed = null;
const isMissionDebugEnabled = () => (
  typeof process !== 'undefined'
  && process.env
  && process.env.NODE_ENV !== 'production'
  && typeof window !== 'undefined'
  && Boolean(window.__MISSION_DEBUG)
);
const missionDebug = (...args) => {
  if (isMissionDebugEnabled()) {
    console.log(...args);
  }
};

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const mergeMissionPayload = (storedValue, incomingValue) => {
  const left = isObject(storedValue) ? storedValue : {};
  const right = isObject(incomingValue) ? incomingValue : {};
  if (!Object.keys(left).length && !Object.keys(right).length) return null;
  return {
    ...left,
    ...right
  };
};

/**
 * Mission data structure:
 * {
 *   fromJourneyGuide: boolean,
 *   missionId: string,
 *   missionTitle: string,
 *   missionRoute: string,
 *   intention: string,
 *   problemText: string,
 *   contextAdvice: string,
 *   timestamp: number
 * }
 */

/**
 * Set mission data when starting a mission from Journey Guide
 * @param {Object} missionData - Mission data object
 * @param {string} missionData.missionId - Mission ID
 * @param {string} missionData.missionTitle - Mission title
 * @param {string} missionData.missionRoute - Mission route
 * @param {string} missionData.intention - User's intention (Pre-Flight Check)
 * @param {string} missionData.problemText - Pre-filled problem text (for Quick Advice)
 * @param {string} missionData.contextAdvice - AI-generated context advice
 */
export const setMissionData = (missionData) => {
  try {
    const data = {
      fromJourneyGuide: true,
      ...missionData,
      timestamp: Date.now()
    };
    
    sessionStorage.setItem(MISSION_DATA_KEY, JSON.stringify(data));
    missionDebug('[MissionDataHelper] Mission data saved:', data);
    return true;
  } catch (error) {
    console.error('[MissionDataHelper] Error saving mission data:', error);
    return false;
  }
};

/**
 * Get mission data for the current page
 * @param {boolean} clearAfterRead - Whether to clear data after reading (default: false)
 * @returns {Object|null} Mission data or null if not found/expired
 */
export const getMissionData = (clearAfterRead = false) => {
  try {
    const stored = sessionStorage.getItem(MISSION_DATA_KEY);
    if (!stored) {
      missionDataCacheRaw = null;
      missionDataCacheParsed = null;
      return null;
    }
    const data = missionDataCacheRaw === stored
      ? missionDataCacheParsed
      : JSON.parse(stored);
    missionDataCacheRaw = stored;
    missionDataCacheParsed = data;
    
    // Check if data is expired
    const age = Date.now() - data.timestamp;
    if (age > MISSION_DATA_EXPIRY) {
      missionDebug('[MissionDataHelper] Mission data expired, clearing');
      clearMissionData();
      return null;
    }

    // Clear if requested
    if (clearAfterRead) {
      clearMissionData();
    }

    return data;
  } catch (error) {
    console.error('[MissionDataHelper] Error reading mission data:', error);
    missionDataCacheRaw = null;
    missionDataCacheParsed = null;
    return null;
  }
};

/**
 * Clear mission data from storage
 */
export const clearMissionData = () => {
  try {
    sessionStorage.removeItem(MISSION_DATA_KEY);
    missionDataCacheRaw = null;
    missionDataCacheParsed = null;
    missionDebug('[MissionDataHelper] Mission data cleared');
  } catch (error) {
    console.error('[MissionDataHelper] Error clearing mission data:', error);
  }
};

/**
 * Check if current page is in "Mission Mode" (came from Journey Guide)
 * @returns {boolean}
 */
export const isMissionMode = () => {
  const data = getMissionData(false);
  return data?.fromJourneyGuide === true;
};

/**
 * Get specific mission data field
 * @param {string} field - Field name to retrieve
 * @returns {any} Field value or null
 */
export const getMissionField = (field) => {
  const data = getMissionData(false);
  return data?.[field] || null;
};

/**
 * React hook for accessing mission data
 * @param {boolean} clearAfterRead - Whether to clear after reading
 * @returns {Object} { missionData, isMission, clearMission }
 */
export const useMissionData = (clearAfterRead = false) => {
  const missionData = getMissionData(clearAfterRead);
  const isMission = missionData?.fromJourneyGuide === true;

  return {
    missionData,
    isMission,
    clearMission: clearMissionData,
    getField: (field) => missionData?.[field] || null
  };
};

/**
 * Helper to prepare navigation state with mission data
 * This combines location.state with mission data for backward compatibility
 * @param {Object} locationState - Current location.state
 * @returns {Object} Combined mission data
 */
export const getCombinedMissionData = (locationState = {}) => {
  const storedData = getMissionData(false);
  
  // Priority: location.state > sessionStorage
  return {
    ...storedData,
    ...locationState,
    actionPayload: mergeMissionPayload(
      storedData?.actionPayload,
      locationState?.navigationState?.actionPayload || locationState?.actionPayload
    ),
    continuity: mergeMissionPayload(
      storedData?.continuity,
      locationState?.navigationState?.continuity || locationState?.continuity
    ),
    transferMeta: mergeMissionPayload(
      storedData?.transferMeta,
      locationState?.transferMeta
    )
  };
};

